import { AssociatedType } from '@fullstory/typeutils';

// Request/response messages, defined as a request type paired with a response
// type. Using a tuple type makes extracting the individual types easy and
// allows for some cleverness to improve type safety in handleMessage().
export type RequestResponseMessage<Req, Resp> = [Req, Resp];

export type AnyRequestResponseMessage = RequestResponseMessage<any, any>;

// Helpers which can extract the request and response types from a
// RequestResponseMessage type.
export type RequestType<M extends AnyRequestResponseMessage> = M extends [infer Req, infer Resp]
  ? Req
  : never;
export type ResponseType<M extends AnyRequestResponseMessage> = M extends [infer Req, infer Resp]
  ? Resp
  : never;

// A helper which can extract the response type for a *specific* request type.
// An example to illustrate the difference:
//   type M = [{tag: 'a'}, {a: number}] | [{tag: 'b', {b: string}];
//   let foo: ResponseType<M>;  // foo has type '{a: number} | {b: string}'.
//   let bar: ResponseTypeFor<M, {tag: 'a'}>;  // bar has type '{a: number}'.
export type ResponseTypeFor<
  M extends AnyRequestResponseMessage,
  Req extends RequestType<M>,
> = M extends [Req, infer Resp] ? Resp : never;

const exceptionProperty = '__request_response_channel_exception__';

// A special response type used to propagate exceptions from the message
// receiver to the message sender. The exception property key would ideally be a
// symbol so we'd know that it could never conflict with a normal property, but
// unfortunately symbols can't be transferred via the structured clone
// algorithm.
type ExceptionResponse = {
  [exceptionProperty]: string;
};

// Either a response or an exception.
export type ResponseOrException<M extends AnyRequestResponseMessage> =
  | ResponseType<M>
  | ExceptionResponse;

// Either a response to a specific request type or an exception.
export type ResponseOrExceptionFor<
  M extends AnyRequestResponseMessage,
  Req extends RequestType<M>,
> = ResponseTypeFor<M, Req> | ExceptionResponse;

function isException<M extends AnyRequestResponseMessage>(
  response: ResponseOrException<M>,
): response is ExceptionResponse {
  if (typeof response !== 'object') {
    return false;
  }
  if (response === null) {
    return false;
  }
  const exceptionResponse: ExceptionResponse = response;
  return exceptionProperty in exceptionResponse;
}

// A protocol that allows us to send request/response messages over some kind of
// link or medium.
export abstract class RequestResponseProtocol<M extends AnyRequestResponseMessage> {
  // The frame type for the kind of link this protocol handles. This is a
  // "frame" in the networking sense - the atomic unit of data that can be sent
  // or received over the link.
  abstract _FrameType: AssociatedType<unknown>;

  // The type of object we send requests to on this link.
  abstract _RequestReceiverType: AssociatedType<unknown>;

  // The type of object we send responses to on this link. Note that this is
  // usually not symmetric with _RequestReceiverType.
  abstract _ResponseReceiverType: AssociatedType<unknown>;

  // Metadata associated with requests or responses. For example, used
  // to transfer Transferables in ./protocol/message.ts.
  abstract _MetadataType: AssociatedType<unknown>;

  // Expose the message type for utilities like ReceiverPool.
  _MessageType!: AssociatedType<M>;

  // Send a strongly typed message to a message receiver. Returns a promise that
  // resolves to the receiver's response.
  async sendMessage<Request extends RequestType<M>>(
    receiver: this['_RequestReceiverType'],
    request: Request,
    metadata?: this['_MetadataType'],
  ): Promise<ResponseTypeFor<M, Request>> {
    const response = await this.sendRequest(receiver, request, metadata);

    // If the receiver threw an exception, reject on the sender side as well.
    // This prevents the sender from hanging forever waiting for a response.
    if (isException(response)) {
      const exception = response[exceptionProperty];
      throw new Error(`Message receiver threw exception: ${exception}`);
    }

    return response;
  }

  async forwardMessage<P extends RequestResponseProtocol<M>>(
    frame: this['_FrameType'],
    otherProtocol: P,
    receiver: P['_RequestReceiverType'],
  ): Promise<void> {
    return this.handleMessage(frame, async request => {
      const response = await otherProtocol.sendMessage(receiver, request);
      return [request, response] as M;
    });
  }

  // Handle a strongly typed message. The handler function must return a promise
  // that resolves to a [request, response] pair; see the comments below for more
  // details on why this is nice.
  async handleMessage(
    frame: this['_FrameType'],
    handler: (req: RequestType<M>) => Promise<M | [...M, this['_MetadataType']]>,
  ): Promise<void> {
    const unpackedRequest = this.readRequest(frame);
    if (!unpackedRequest) {
      return;
    }
    const [request, responseReceiver] = unpackedRequest;

    try {
      // Let the handler process the message. This is a little tricky: the handler
      // must return both the original request *and* the response.  This trick will
      // prevent `return [A, B]` from type checking in the handler unless [A, B] is
      // a request/response type pair in M. Importantly, if M is `[A, B] | [C, D]`,
      // then `return [A, D]` will not compile, which wouldn't be the case if the
      // handler just returned ResponseType<M>.
      const [, response, metadata]: M | [...M, this['_MetadataType']] = await handler(request);

      this.sendResponse(responseReceiver, response, metadata);
    } catch (e) {
      // The receiver threw an exception. Respond to the sender so it doesn't hang
      // forever.
      this.sendResponse(responseReceiver, { [exceptionProperty]: (e as Error).toString() });

      // We don't want to swallow the exception here; rethrow.
      throw e;
    }
  }

  // Read a frame that may contain a request and return either:
  //  - A (request message, response receiver) pair, if the frame does contain a
  //  request. The response receiver is essentially a "return address" that can
  //  be used to send a response back to the sender of the request.
  //  - 'undefined', if the frame does not contain a request. This isn't an
  //  error; some links mix requests with other kinds of frames.
  protected abstract readRequest(
    frame: this['_FrameType'],
  ): [RequestType<M>, this['_ResponseReceiverType']] | undefined;

  // Send a request on the link. Returns a promise that resolves to a response
  // message.
  protected abstract sendRequest<Request extends RequestType<M>>(
    receiver: this['_RequestReceiverType'],
    request: Request,
    metadata?: this['_MetadataType'],
  ): Promise<ResponseOrExceptionFor<M, Request>>;

  // Send a response on the link.
  protected abstract sendResponse(
    receiver: this['_ResponseReceiverType'],
    response: ResponseOrException<M>,
    metadata?: this['_MetadataType'],
  ): void;
}
